import React from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, StarIcon, ChevronRightIcon } from 'lucide-react';
import { getGenreColors } from '../../utils/genreColors';
interface AuthorCardProps {
  slug: string;
  name: string;
  avatar: string;
  bio: string;
  location?: string; // Kept optional for compatibility, but not used in UI
  genres: string[];
  bookCount: number;
  rating: number;
  reviewCount?: number;
  hook?: string;
  readerExperience?: string[];
  badges?: string[];
  variant?: 'list' | 'grid';
}
const MAX_VISIBLE_GENRES_LIST = 3;
const MAX_VISIBLE_GENRES_GRID = 2;
const GenreTag: React.FC<{
  genre: string;
}> = ({
  genre
}) => {
  const colors = getGenreColors(genre);
  return <span className="px-2.5 py-0.5 text-xs font-medium rounded-full border transition-colors" style={{
    backgroundColor: colors.bgLight,
    color: colors.text,
    borderColor: `${colors.bg}30`
  }} data-id="element-846" data-mp="3uwryl6t80a0s">
      {genre}
    </span>;
};
const OverflowTag: React.FC<{
  count: number;
}> = ({
  count
}) => <span className="px-2.5 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full border border-gray-200" data-id="element-847" data-mp="3k2q0cuspnwz2">
    +{count} more
  </span>;
const AuthorCard: React.FC<AuthorCardProps> = ({
  slug,
  name,
  avatar,
  bio,
  genres,
  bookCount,
  rating,
  reviewCount,
  hook,
  readerExperience,
  badges,
  variant = 'list'
}) => {
  // Auto-generate hook if missing
  const displayHook = hook || (genres.length > 0 ? `${genres[0]} author creating immersive worlds.` : 'Bestselling author on LitLaunch.');
  // Grid Variant (Scan Mode)
  if (variant === 'grid') {
    return <Link to={`/author/${slug}`} className="block group h-full" data-id="element-848" data-mp="3t3o4re72a6i6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-full flex flex-col p-5 text-center transition-shadow duration-200 hover:shadow-md" data-id="element-849" data-mp="1x4mxf2t5gad">
          {/* 1. Avatar */}
          <div className="mx-auto mb-3" data-id="element-850" data-mp="uf8urr33mv3r">
            <img src={avatar} alt={name} className="w-20 h-20 rounded-full object-cover ring-2 ring-gray-100 group-hover:ring-indigo-100 transition-all duration-300" data-id="element-851" data-mp="uga8k3bi1vp6" />
          </div>

          {/* 2. Name */}
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-200 mb-2 line-clamp-1" data-id="element-852" data-mp="uaow2vfduele">
            {name}
          </h3>

          {/* 3. Rating & Book Count */}
          <div className="flex items-center justify-center gap-3 text-xs text-gray-500 mb-3" data-id="element-853" data-mp="lcwfzp1u72wy">
            <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100 text-amber-700 font-medium" data-id="element-854" data-mp="gjtm1ft5drg8">
              <StarIcon className="h-3 w-3 fill-amber-500 text-amber-500" data-id="element-855" data-mp="6mfgib8rin2p" />
              <span data-id="element-856" data-mp="6kcf6v25t7io">{rating.toFixed(1)}</span>
              {reviewCount && <span className="text-amber-600/70 font-normal" data-id="element-857" data-mp="2kanneq51uh4">
                  ({reviewCount})
                </span>}
            </div>
            <span className="text-gray-300" data-id="element-858" data-mp="299cm229fujvv">·</span>
            <div className="flex items-center" data-id="element-859" data-mp="298kv54qsdabx">
              <BookIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-860" data-mp="2e7xgtm5x8bcx" />
              {bookCount} Books
            </div>
          </div>

          {/* 4. Genre Tags */}
          <div className="flex flex-wrap justify-center gap-1.5 mb-3" data-id="element-861" data-mp="2i7oapyqqv7lo">
            {genres.slice(0, MAX_VISIBLE_GENRES_GRID).map(genre => <GenreTag key={genre} genre={genre} data-id="element-862" data-mp="1qj4mtv8v0dky" />)}
            {genres.length > MAX_VISIBLE_GENRES_GRID && <OverflowTag count={genres.length - MAX_VISIBLE_GENRES_GRID} data-id="element-863" data-mp="1ukzrn9q0193h" />}
          </div>

          {/* 5. Hook */}
          <p className="text-sm text-gray-600 line-clamp-2 mb-4 flex-grow" data-id="element-864" data-mp="1zh6omd0f3a9a">
            {displayHook}
          </p>

          {/* 6. Identity Micro-Badges */}
          {badges && badges.length > 0 && <div className="flex flex-wrap justify-center gap-1.5 mb-4" data-id="element-865" data-mp="16sriyh36e4am">
              {badges.slice(0, 2).map(badge => <span key={badge} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded-full border border-gray-200 uppercase tracking-wide" data-id="element-866" data-mp="3efucneawx5h4">
                  {badge}
                </span>)}
            </div>}

          {/* 7. CTA */}
          <div className="mt-auto pt-3 border-t border-gray-50" data-id="element-867" data-mp="3igec4oiykl6f">
            <span className="text-sm font-medium text-indigo-600 group-hover:text-indigo-700 flex items-center justify-center gap-1" data-id="element-868" data-mp="10oc0mh3h116">
              View Books <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-869" data-mp="3s7il97lydbtc" />
            </span>
          </div>
        </div>
      </Link>;
  }
  // List Variant (Expanded / Evaluation Mode)
  return <Link to={`/author/${slug}`} className="block group" data-id="element-870" data-mp="6129jal2ksky">
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden p-5 sm:p-6 transition-shadow duration-200 hover:shadow-md" data-id="element-871" data-mp="2bsz7os4hpjxv">
        <div className="flex flex-col sm:flex-row gap-5 sm:gap-6" data-id="element-872" data-mp="27ti3s34pouut">
          {/* Avatar Column */}
          <div className="flex-shrink-0" data-id="element-873" data-mp="1xoifqkrdw3q6">
            <img src={avatar} alt={name} className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover ring-2 ring-gray-100 group-hover:ring-indigo-100 transition-all duration-300" data-id="element-874" data-mp="21pc4fgcnhmk4" />
          </div>

          {/* Content Column */}
          <div className="flex-1 min-w-0" data-id="element-875" data-mp="1suxi4ttpdbri">
            {/* 1. Header Row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3" data-id="element-876" data-mp="1dyoihr5wgwek">
              <h3 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-200" data-id="element-877" data-mp="1izvtdz3cabpg">
                {name}
              </h3>
              <div className="flex items-center gap-2" data-id="element-878" data-mp="19xw6wr9k2ff4">
                <div className="flex items-center gap-1 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100 text-amber-700 text-xs font-bold" data-id="element-879" data-mp="1a0iegqwg4uxc">
                  <StarIcon className="h-3 w-3 fill-amber-500 text-amber-500" data-id="element-880" data-mp="34cp4gvu01g9p" />
                  <span data-id="element-881" data-mp="31ximckzkg3oq">{rating.toFixed(1)}</span>
                  {reviewCount && <span className="text-amber-600/70 font-normal" data-id="element-882" data-mp="2xw6su4dkmyb9">
                      ({reviewCount})
                    </span>}
                </div>
                <span className="text-gray-300 text-sm" data-id="element-883" data-mp="2sz6kfmt91jgz">·</span>
                <span className="text-xs text-gray-500 font-medium" data-id="element-884" data-mp="2nz0zephhiv7v">
                  {bookCount} Books
                </span>
                {genres[0] && <>
                    <span className="text-gray-300 text-sm" data-id="element-885" data-mp="3gmeoy2iusohy">·</span>
                    <span className="text-xs text-gray-500 font-medium" data-id="element-886" data-mp="3bvycakmux6wn">
                      {genres[0]} Author
                    </span>
                  </>}
              </div>
            </div>

            {/* 2. Identity Block (Confidence Anchor) */}
            {readerExperience && readerExperience.length > 0 && <div className="mb-3 bg-slate-50 rounded-lg px-3 py-2 border-l-2 border-indigo-400" data-id="element-887" data-mp="3f3irvry8yv8">
                <div className="flex flex-wrap items-baseline gap-x-2" data-id="element-888" data-mp="asck6mogoix9">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500" data-id="element-889" data-mp="fq47t4i2pivn">
                    Reader Experience
                  </span>
                  <span className="text-sm font-medium text-slate-700" data-id="element-890" data-mp="krjvl4bmy3hq">
                    {readerExperience.join(' · ')}
                  </span>
                </div>
              </div>}

            {/* 3. Bio */}
            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-4" data-id="element-891" data-mp="yoypal8x8xfi">
              {bio}
            </p>

            {/* 4. Genre Tags & CTA Row */}
            <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-50" data-id="element-892" data-mp="13gqfuvs2v35n">
              <div className="flex flex-wrap gap-2" data-id="element-893" data-mp="18e9tbtgybbwj">
                {genres.slice(0, MAX_VISIBLE_GENRES_LIST).map(genre => <GenreTag key={genre} genre={genre} data-id="element-894" data-mp="1hfpx3id9gfrs" />)}
                {genres.length > MAX_VISIBLE_GENRES_LIST && <OverflowTag count={genres.length - MAX_VISIBLE_GENRES_LIST} data-id="element-895" data-mp="22pmz0tg55phv" />}
              </div>

              <div className="flex-shrink-0" data-id="element-896" data-mp="2coucrhqsfm5i">
                <span className="text-sm font-medium text-indigo-600 group-hover:text-indigo-700 flex items-center gap-1" data-id="element-897" data-mp="2cnjchrwjwwj5">
                  View Books <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-898" data-mp="27nonxv090j19" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>;
};
export default AuthorCard;